// client/src/coms/personalisation/PersonalisationSettings.js
// One personalisation area in Settings → Appearance: Theme, Layout, Tabs & split, and This server.
import React, { useState } from 'react';
import { usePersonalisation } from './PersonalisationProvider';
import ThemeSettings from './ThemeSettings';
import LayoutSettings from './LayoutSettings';
import ServerPrefsSettings from './ServerPrefsSettings';
import { Row, Section, Switch } from './settingsControls';
import './personalisationSettings.css';

export const SHORTCUTS = [
  ['Alt+Shift+→ / ←', 'Next / previous conversation tab'],
  ['Alt+Shift+1 … 9', 'Go to tab 1 – 9'],
  ['Alt+Shift+W', 'Close the current tab'],
  ['Alt+Shift+T', 'Reopen the last closed tab'],
  ['Alt+Shift+P', 'Pin or unpin the current tab'],
  ['Alt+Shift+S', 'Open or close split view'],
  ['Alt+Shift+X', 'Move focus between split panes'],
  ['Ctrl/⌘ + click, middle click', 'Open a channel in a new tab'],
  ['Shift+← / → on a tab', 'Move the tab'],
];

function TabsSettings() {
  const pz = usePersonalisation();
  return (
    <Section id="vx-tabs" title="Tabs and split view" description="Keep several conversations open, from any server, and view two side by side.">
      <Row label="Reopen my tabs" hint="Restore the conversations you had open when you come back." settingId="tabs" scope="account">
        <Switch label="Reopen my tabs" checked={pz.globalPrefs.tabs.restore} onChange={(v) => pz.setTabsValue('restore', v)} testId="tabs-restore" />
      </Row>
      <div className="vx-set-hint">Ctrl/⌘ + click or middle-click a channel to open it in a new tab. Keyboard shortcuts for tabs and split view are listed under Keyboard Shortcuts. On phones one conversation is shown at a time; your tabs, drafts and places are kept.</div>
    </Section>
  );
}

const SECTIONS = [
  { id: 'theme', label: 'Theme' },
  { id: 'layout', label: 'Layout' },
  { id: 'tabs', label: 'Tabs & split' },
  { id: 'server', label: 'This server' },
];

export function TabsSettingsSection() {
  return <TabsSettings />;
}

export default function PersonalisationSettings({ serverKey, serverName, channels, initialSection, onCustomiseOnScreen }) {
  const pz = usePersonalisation();
  const [section, setSection] = useState(SECTIONS.some((s) => s.id === initialSection) ? initialSection : 'theme');
  return (
    <div className="vx-pz vx-set" data-testid="personalisation-settings">
      {pz.syncError ? (
        <div className="vx-acct-error" role="status">
          {pz.syncError}
        </div>
      ) : null}
      <div className="vx-set-tabs" role="tablist" aria-label="Appearance and layout">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            id={`vx-set-tab-${s.id}`}
            aria-selected={section === s.id}
            aria-controls={`vx-set-panel-${s.id}`}
            tabIndex={section === s.id ? 0 : -1}
            className={`vx-set-tab${section === s.id ? ' is-on' : ''}`}
            onClick={() => setSection(s.id)}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
              e.preventDefault();
              const i = SECTIONS.findIndex((x) => x.id === section);
              const next = SECTIONS[(i + (e.key === 'ArrowRight' ? 1 : -1) + SECTIONS.length) % SECTIONS.length];
              setSection(next.id);
              const el = document.getElementById(`vx-set-tab-${next.id}`);
              if (el) el.focus();
            }}
            data-testid={`pz-section-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`vx-set-panel-${section}`} aria-labelledby={`vx-set-tab-${section}`}>
        {section === 'theme' ? <ThemeSettings /> : null}
        {section === 'layout' ? <LayoutSettings onCustomiseOnScreen={onCustomiseOnScreen} /> : null}
        {section === 'tabs' ? <TabsSettings /> : null}
        {section === 'server' ? <ServerPrefsSettings serverKey={serverKey} serverName={serverName} channels={channels} /> : null}
      </div>
    </div>
  );
}
